import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PlayerView from './PlayerView';
import { ICONS } from '../constants';
import { CommentData, Shot, Project, DrawingTool, Timecode, MentionUser } from '../types';
import { secondsToTimecode } from '../utils/timecode';
import { API_CONFIG } from '../config';
import { useVoiceRecording } from '../hooks/useVoiceRecording';
import { captureVideoFrame, compositeDrawingWithFrame } from '../utils/videoFrameCapture';

interface AnnotationModalProps {
  shot: {
    id: string;
    shotNumber: number;
    shotName?: string;
    thumbnailUrl?: string;
    videoUrl?: string;
    annotations?: any[];
  };
  onClose: () => void;
  onSave: (shotId: string, dataUrl: string | null) => Promise<void>;
  onCommentsCountChange?: (shotId: string, count: number) => void;
  commentId?: string;
}

const countAllComments = (comments: CommentData[]): number => {
  let n = 0;
  const walk = (list: CommentData[]) => {
    for (const c of list) {
      n += 1;
      if (c.replies?.length) walk(c.replies);
    }
  };
  walk(comments);
  return n;
};

/** Merge a newly created root comment into the nested list from the API */
const upsertRootComment = (list: CommentData[], comment: CommentData): CommentData[] => {
  const exists = list.some((c) => c.id === comment.id);
  if (exists) {
    return list.map((c) => (c.id === comment.id ? { ...c, ...comment, replies: c.replies || [] } : c));
  }
  if (comment.parentCommentId) {
    // Attach under parent if present; otherwise reload will fix
    const attach = (nodes: CommentData[]): CommentData[] =>
      nodes.map((n) => {
        if (n.id === comment.parentCommentId) {
          const replies = [...(n.replies || []), { ...comment, replies: [] }];
          return { ...n, replies };
        }
        if (n.replies?.length) {
          return { ...n, replies: attach(n.replies) };
        }
        return n;
      });
    return attach(list);
  }
  return [...list, { ...comment, replies: comment.replies || [] }];
};

const AnnotationModal: React.FC<AnnotationModalProps> = ({
  shot,
  onClose,
  onSave: _onSave,
  onCommentsCountChange,
  commentId,
}) => {
  const [comments, setComments] = useState<CommentData[]>([]);
  const [currentUser, setCurrentUser] = useState<{ id: string; name: string } | null>(null);
  const [mentionUsers, setMentionUsers] = useState<MentionUser[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [seekTime, setSeekTime] = useState<number | null>(null);
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const [clearDrawingSignal, setClearDrawingSignal] = useState(0);
  const [undoSignal, setUndoSignal] = useState(0);
  const [drawingTool, setDrawingTool] = useState<DrawingTool>('pencil');
  const [drawingColor, setDrawingColor] = useState('#ef4444');
  const [currentDrawing, setCurrentDrawing] = useState<string | null>(null);
  const currentDrawingRef = useRef<string | null>(null);
  const pendingRecordingRef = useRef<{ blob: Blob; duration: number } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const seekClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentTimecode: Timecode = useMemo(() => secondsToTimecode(currentTime, 24), [currentTime]);

  const shotTitle = useMemo(() => {
    const name = shot.shotName?.trim();
    if (name) return name;
    return `Shot #${shot.shotNumber}`;
  }, [shot.shotName, shot.shotNumber]);

  useEffect(() => {
    currentDrawingRef.current = currentDrawing;
  }, [currentDrawing]);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('spellbook_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const reloadComments = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/annotations/shots/${shot.id}/comments`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        const loaded = data.comments || [];
        setComments(loaded);
        onCommentsCountChange?.(shot.id, countAllComments(loaded));
        return loaded as CommentData[];
      }
      console.error('Failed to load comments:', await response.text());
    } catch (error) {
      console.error('Error loading comments:', error);
    }
    return null;
  }, [shot.id, authHeaders, onCommentsCountChange]);

  const submitComment = useCallback(
    async (
      text: string,
      recording?: { blob: Blob; duration: number },
      attachment?: File,
      parentCommentId?: string
    ) => {
      // Auth is Bearer token; do not gate on currentUser state (may lag / fail to parse)
      const drawingSnapshot = currentDrawingRef.current;

      // Capture video frame + optional composite with drawing for comment thumbnail
      let frameThumbnailUrl: string | null = captureVideoFrame(videoRef.current);
      if (frameThumbnailUrl && drawingSnapshot) {
        try {
          frameThumbnailUrl = await compositeDrawingWithFrame(frameThumbnailUrl, drawingSnapshot);
        } catch (err) {
          console.error('Failed to composite drawing with frame:', err);
          // keep raw frame
        }
      }

      const form = new FormData();
      form.append('timecode', JSON.stringify(currentTimecode));
      form.append('timeInSeconds', String(currentTime));
      form.append('text', text || '');
      if (drawingSnapshot) form.append('drawingDataUrl', drawingSnapshot);
      if (frameThumbnailUrl) form.append('frameThumbnailUrl', frameThumbnailUrl);
      if (parentCommentId) form.append('parentCommentId', parentCommentId);
      if (recording) {
        form.append('recording', recording.blob, 'recording.webm');
        form.append('recordingDuration', String(recording.duration));
      }
      if (attachment) {
        form.append('attachment', attachment, attachment.name);
      }

      const response = await fetch(`${API_CONFIG.API_URL}/annotations/shots/${shot.id}/comments`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error('Failed to create comment:', errText);
        let message = errText || `Failed to create comment (${response.status})`;
        try {
          const parsed = JSON.parse(errText) as { error?: string; message?: string };
          message = parsed.error || parsed.message || message;
        } catch {
          // keep raw text
        }
        throw new Error(message);
      }

      const data = await response.json();
      const created = data.comment as CommentData | undefined;

      if (created) {
        setComments((prev) => {
          const next = upsertRootComment(prev, created);
          onCommentsCountChange?.(shot.id, countAllComments(next));
          return next;
        });
      }

      // Refresh from server (best-effort); keep optimistic list if reload fails
      await reloadComments();

      setCurrentDrawing(null);
      currentDrawingRef.current = null;
      setIsDrawingEnabled(false);
      setClearDrawingSignal((prev) => prev + 1);
      pendingRecordingRef.current = null;
    },
    [
      currentTimecode,
      currentTime,
      shot.id,
      authHeaders,
      reloadComments,
      onCommentsCountChange,
    ]
  );

  const {
    isRecording,
    recordingTime,
    startRecording,
    stopRecording,
  } = useVoiceRecording({
    onRecordingComplete: (blob, durationMs) => {
      pendingRecordingRef.current = { blob, duration: durationMs / 1000 };
      void submitComment('[Recording]', { blob, duration: durationMs / 1000 }).catch((err) =>
        console.error(err)
      );
    },
  });

  useEffect(() => {
    if (isPlaying && isDrawingEnabled) {
      setIsDrawingEnabled(false);
      setCurrentDrawing(null);
      currentDrawingRef.current = null;
      setClearDrawingSignal((prev) => prev + 1);
    }
  }, [isPlaying, isDrawingEnabled]);

  const videoAsset = useMemo(
    () => ({
      id: shot.id,
      url: shot.videoUrl,
    }),
    [shot.id, shot.videoUrl]
  );

  const shotData: Shot = useMemo(
    () => ({
      id: shot.id,
      biddingSheetId: '',
      shotNumber: shot.shotNumber,
      shotName: shot.shotName || `Shot #${shot.shotNumber}`,
      thumbnailUrl: shot.thumbnailUrl,
      videoUrl: shot.videoUrl,
      annotations: shot.annotations || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
    [shot.id, shot.shotNumber, shot.shotName, shot.thumbnailUrl, shot.videoUrl, shot.annotations]
  );

  const mockProject: Project = {
    id: '',
    name: 'Current Project',
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    status: 'In Progress',
    createdBy: '',
    createdAt: new Date().toISOString(),
  };

  useEffect(() => {
    try {
      const userDataStr = localStorage.getItem('spellbook_user');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        setCurrentUser({
          id: userData.id,
          name: userData.username || userData.email || 'Unknown User',
        });
      }
    } catch (error) {
      console.error('Failed to parse user data from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await fetch(`${API_CONFIG.API_URL}/auth/users/management`, {
          headers: authHeaders(),
        });
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : data.users || [];
          setMentionUsers(
            list.map((u: any) => ({
              id: u.id,
              name: u.username || u.name || u.email || 'User',
              email: u.email,
            }))
          );
        }
      } catch (error) {
        console.error('Failed to load mention users:', error);
      }
    };
    loadUsers();
  }, [authHeaders]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const loaded = await reloadComments();
      if (cancelled || !loaded || !commentId) return;
      const flat: CommentData[] = [];
      const walk = (list: CommentData[]) => {
        for (const c of list) {
          flat.push(c);
          if (c.replies) walk(c.replies);
        }
      };
      walk(loaded);
      const target = flat.find((c) => c.id === commentId);
      if (target) {
        setSeekTime(target.timeInSeconds);
        setCurrentTime(target.timeInSeconds);
        setTimeout(() => {
          const el = document.getElementById(`comment-${commentId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-yellow-500', 'ring-opacity-75');
            setTimeout(() => el.classList.remove('ring-2', 'ring-yellow-500', 'ring-opacity-75'), 2000);
          }
        }, 500);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // Only re-load when shot/comment deep-link changes — not when reloadComments identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shot.id, commentId]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // MagicBox-style seek: update both video seekTime and UI timeInSeconds (scrubber)
  const handleSeek = useCallback((time: number) => {
    const clamped = Math.max(0, time);
    if (seekClearTimeoutRef.current) {
      clearTimeout(seekClearTimeoutRef.current);
    }
    setSeekTime(clamped);
    setCurrentTime(clamped);
    seekClearTimeoutRef.current = setTimeout(() => {
      setSeekTime(null);
    }, 50);
  }, []);

  useEffect(() => {
    return () => {
      if (seekClearTimeoutRef.current) clearTimeout(seekClearTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleAddComment = async (
    text: string,
    recording?: { blob: Blob; duration: number },
    attachment?: File
  ) => {
    await submitComment(text, recording, attachment);
  };

  const handleReply = async (parentId: string, text: string, file?: File) => {
    await submitComment(text, undefined, file, parentId);
  };

  const handleDeleteComment = async (id: string) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/annotations/shots/${shot.id}/comments/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (response.ok) {
        await reloadComments();
      } else {
        console.error('Failed to delete comment:', await response.text());
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleUpdateComment = async (id: string, newText: string) => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/annotations/shots/${shot.id}/comments/${id}`, {
        method: 'PATCH',
        headers: {
          ...authHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: newText }),
      });
      if (response.ok) {
        await reloadComments();
      } else {
        console.error('Failed to update comment:', await response.text());
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleToggleDrawing = (enabled: boolean) => {
    if (enabled) setIsPlaying(false);
    setIsDrawingEnabled(enabled);
    if (!enabled) {
      setCurrentDrawing(null);
      currentDrawingRef.current = null;
      setClearDrawingSignal((prev) => prev + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      {/* Title / close row — keeps X off the comments sort controls */}
      <div className="flex-shrink-0 h-12 px-4 flex items-center border-b border-gray-700 bg-gray-900 relative z-[60]">
        <div className="w-10 flex-shrink-0" aria-hidden />
        <div className="flex-1 text-center min-w-0 px-2">
          <h2 className="text-white font-semibold text-sm sm:text-base truncate" title={shotTitle}>
            {shotTitle}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 flex-shrink-0 flex items-center justify-center hover:bg-gray-700 rounded transition"
          title="Close (ESC)"
        >
          <ICONS.X className="w-6 h-6 text-gray-300" />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex overflow-hidden">
        <PlayerView
          asset={videoAsset}
          shot={shotData}
          project={mockProject}
          sequence={null}
          comments={comments}
          isPlaying={isPlaying}
          seekTime={seekTime}
          isDrawingEnabled={isDrawingEnabled}
          clearDrawingSignal={clearDrawingSignal}
          undoSignal={undoSignal}
          effectiveFps={24}
          drawingTool={drawingTool}
          drawingColor={drawingColor}
          onTimeUpdate={setCurrentTime}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          onDrawingChange={(dataUrl) => {
            setCurrentDrawing(dataUrl);
            currentDrawingRef.current = dataUrl;
          }}
          onSeek={handleSeek}
          onDeleteComment={handleDeleteComment}
          onEditComment={handleUpdateComment}
          onJumpAndShow={(comment) => {
            handleSeek(comment.timeInSeconds);
          }}
          onReply={handleReply}
          timeInSeconds={currentTime}
          currentTimecode={currentTimecode}
          onAddComment={handleAddComment}
          onToggleDrawing={handleToggleDrawing}
          isRecording={isRecording}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
          recordingTime={recordingTime}
          setDrawingTool={setDrawingTool}
          setDrawingColor={setDrawingColor}
          onUndo={() => setUndoSignal((p) => p + 1)}
          onClear={() => setClearDrawingSignal((p) => p + 1)}
          currentUserId={currentUser?.id}
          users={mentionUsers}
          videoRef={videoRef}
        />
      </div>
    </div>
  );
};

export default AnnotationModal;
